import React, { useRef } from 'react';
import { Camera, Type, Paintbrush, Plus, RotateCcw, HelpCircle, X, Download, Upload, Image, FileText } from 'lucide-react';
import { TextElement, ImageElement } from '../types';
import { useStore } from '../store';
import { exportToPNG, exportToPDF, downloadFile } from '../utils/export';

interface ToolbarProps {
  activeTab: string;
  onTabChange: (tab: string) => void;
  textElements: TextElement[];
  imageElements: ImageElement[];
  transparentBackground?: boolean;
  canvasRef?: React.RefObject<fabric.Canvas>;
  containerWidth?: number;
  containerHeight?: number;
  canvasWidth?: number;
  canvasHeight?: number;
  canvasLeft?: number;
  canvasTop?: number;
  objectBackground?: string;
  hideExportButtons?: boolean;
}

export const Toolbar: React.FC<ToolbarProps> = ({
  activeTab,
  onTabChange,
  textElements,
  imageElements,
  transparentBackground = false,
  canvasRef,
  containerWidth = 500,
  containerHeight = 500,
  canvasWidth = 256,
  canvasHeight = 384,
  canvasLeft = 196,
  canvasTop = 190,
  objectBackground,
  hideExportButtons = false
}) => {
  const { exportDesign, importDesign, resetDesign } = useStore();
  const fileInputRef = useRef<HTMLInputElement>(null);

  const baseTabs = !transparentBackground ? [
    { id: 'background', icon: Paintbrush, label: 'Couleur de fond' },
  ] : [];

  const allTabs = [
    ...baseTabs,
    ...textElements.map((el) => ({
      id: `text-${el.id}`,
      icon: Type,
      label: `Texte ${textElements.indexOf(el) + 1}`,
    })),
    ...imageElements.map((el) => ({
      id: `photo-${el.id}`,
      icon: Camera,
      label: `Photo ${imageElements.indexOf(el) + 1}`,
    })),
    { id: 'add', icon: Plus, label: 'Ajouter' },
  ];

  const handleExport = () => {
    const design = exportDesign();
    const blob = new Blob([JSON.stringify(design, null, 2)], { type: 'application/json' });
    downloadFile(blob, 'design.json');
  };

  const handleImport = () => {
    fileInputRef.current?.click();
  };

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        const design = JSON.parse(e.target?.result as string);
        importDesign(design);
      } catch (error) {
        console.error('Failed to parse design file:', error);
      }
    };
    reader.readAsText(file);
  };

  const handleExportPNG = async () => {
    if (!canvasRef?.current) return;
    
    const pngData = await exportToPNG({
      canvas: canvasRef.current,
      containerWidth,
      containerHeight,
      canvasWidth,
      canvasHeight,
      canvasLeft,
      canvasTop,
      objectBackground
    });
    
    downloadFile(pngData, 'design-preview.png');
  };

  const handleExportPDF = async () => {
    if (!canvasRef?.current) return;
    
    const pdfBlob = await exportToPDF({
      canvas: canvasRef.current,
      containerWidth,
      containerHeight,
      canvasWidth,
      canvasHeight,
      canvasLeft,
      canvasTop
    });
    
    downloadFile(pdfBlob, 'design-print.pdf');
  };

  return (
    <div className="flex items-center justify-between w-full bg-white border-b border-gray-200 px-4 py-2">
      <div className="flex space-x-4 overflow-x-auto">
        {allTabs.map((tool) => {
          const Icon = tool.icon;
          return (
            <button
              key={tool.id}
              onClick={() => onTabChange(tool.id)}
              className={`flex flex-col items-center px-4 py-2 text-sm whitespace-nowrap ${
                activeTab === tool.id ? 'text-red-500' : 'text-gray-600'
              }`}
            >
              <Icon className="w-5 h-5 mb-1" />
              <span>{tool.label}</span>
            </button>
          );
        })}
      </div>
      {!hideExportButtons && (
        <div className="flex space-x-2">
          <input
            type="file"
            ref={fileInputRef}
            onChange={handleFileChange}
            accept="application/json"
            className="hidden"
          />
          <button 
            onClick={handleExportPNG}
            className="p-2 text-gray-600 hover:text-gray-800 customizer-export-btn"
            title="Exporter en PNG"
          >
            <Image className="w-5 h-5" />
          </button>
          <button 
            onClick={handleExportPDF}
            className="p-2 text-gray-600 hover:text-gray-800 customizer-export-btn"
            title="Exporter en PDF"
          >
            <FileText className="w-5 h-5" />
          </button>
          <button 
            onClick={handleExport}
            className="p-2 text-gray-600 hover:text-gray-800 customizer-export-btn"
            title="Exporter le design"
          >
            <Download className="w-5 h-5" />
          </button>
          <button 
            onClick={handleImport}
            className="p-2 text-gray-600 hover:text-gray-800 customizer-export-btn"
            title="Importer un design"
          >
            <Upload className="w-5 h-5" />
          </button>
          <button 
            onClick={resetDesign}
            className="p-2 text-gray-600 hover:text-gray-800"
            title="Réinitialiser"
          >
            <RotateCcw className="w-5 h-5" />
          </button>
          <button className="p-2 text-gray-600 hover:text-gray-800">
            <HelpCircle className="w-5 h-5" />
          </button>
          <button className="p-2 text-gray-600 hover:text-gray-800">
            <X className="w-5 h-5" />
          </button>
        </div>
      )}
    </div>
  );
};